/**
 * W3C Trace Context propagation for the SPA.
 *
 * Frontend mints a fresh `trace-id` per user-action (route navigation) and a
 * fresh `span-id` per outbound HTTP request, packing both into the W3C
 * `traceparent` header (`00-<32hex>-<16hex>-01`). Backend services parse the
 * header and use `trace-id` as the request-scoped correlation ID — that one
 * ID then stitches together every log line, Pub/Sub message, and worker call
 * triggered by the original click.
 *
 * Action boundary: route navigation. The `useTraceOnRouteChange()` hook
 * (mounted in App.tsx at the router root) calls `startNewTrace()` on every
 * location change, so each page navigation starts a fresh trace. In-page
 * actions (button clicks within the same route) share the route's trace-id —
 * acceptable granularity for the alert→user pivot use case.
 *
 * Backwards compat: `getCorrelationId()` is preserved as a thin wrapper around
 * `getCurrentTraceId()` so existing call sites that send `x-correlation-id`
 * continue to work. The backend's correlationIdMiddleware accepts both
 * headers (traceparent wins when both present).
 *
 * Tier 2 future: when the OpenTelemetry browser SDK lands (audit doc Phase 4),
 * this module's responsibilities move to the SDK's tracer + automatic span
 * creation around fetch / route change. The `traceparent` wire format is
 * unchanged, so Tier 2 is a swap-in, not a redo.
 */

const TRACE_ID_BYTES = 16;
const SPAN_ID_BYTES = 8;
const VERSION = "00";
const SAMPLED_FLAG = "01";

let currentTraceId: string | null = null;

function randomHex(byteCount: number): string {
  const bytes = new Uint8Array(byteCount);
  crypto.getRandomValues(bytes);
  let out = "";
  for (const b of bytes) {
    out += b.toString(16).padStart(2, "0");
  }
  return out;
}

/** W3C trace-id = 32 lowercase hex chars (16 bytes), must not be all zeros. */
function generateTraceId(): string {
  let id = randomHex(TRACE_ID_BYTES);
  // Spec forbids the all-zeros trace-id. crypto.getRandomValues makes this
  // astronomically unlikely, but the check is one comparison.
  while (/^0+$/.test(id)) {
    id = randomHex(TRACE_ID_BYTES);
  }
  return id;
}

/** W3C span-id = 16 lowercase hex chars (8 bytes), must not be all zeros. */
function generateSpanId(): string {
  let id = randomHex(SPAN_ID_BYTES);
  while (/^0+$/.test(id)) {
    id = randomHex(SPAN_ID_BYTES);
  }
  return id;
}

/** Mint a fresh trace-id and store it as the current per-action trace. */
export function startNewTrace(): string {
  currentTraceId = generateTraceId();
  return currentTraceId;
}

/** Get the current trace-id, minting one if no trace has been started yet. */
export function getCurrentTraceId(): string {
  if (!currentTraceId) {
    currentTraceId = generateTraceId();
  }
  return currentTraceId;
}

/**
 * Build a W3C traceparent header value with the current trace-id and a fresh
 * span-id. Each outbound request gets its own span-id; trace-id is shared
 * across all requests within the same user action.
 *
 * Format: `00-<32-hex-trace-id>-<16-hex-span-id>-01`
 *   - `00`  = version
 *   - flags = `01` (sampled)
 */
export function generateTraceparent(): string {
  const traceId = getCurrentTraceId();
  const spanId = generateSpanId();
  return `${VERSION}-${traceId}-${spanId}-${SAMPLED_FLAG}`;
}

/**
 * Backwards-compat: returns the current trace-id under the old name. Existing
 * call sites that send `x-correlation-id` continue to work; the value they
 * send is the same trace-id the backend would extract from a traceparent
 * header.
 */
export function getCorrelationId(): string {
  return getCurrentTraceId();
}

/** Test-only helper to clear the module-level trace state between tests. */
export function _resetTraceForTest(): void {
  currentTraceId = null;
}
